import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Custom element that manages a sticky add to cart button for mobile product pages.
 * Shows at the bottom of the page and hides when the original button is visible.
 */
export class StickyAddToCart extends Component {
  #observer = null;
  #scrollHandler = null;
  #variantUpdateHandler = null;
  #resizeHandler = null;
  #originalButton = null;
  #form = null;
  #productFormComponent = null;
  #isVisible = false;
  #productId = null;

  connectedCallback() {
    super.connectedCallback();
    
    this.#productId = this.dataset.productId;
    if (!this.#productId) return;

    // Only show on mobile
    if (window.innerWidth >= 750) {
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#initialize());
    } else {
      this.#initialize();
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #initialize() {
    // Find the product form component
    this.#productFormComponent = document.querySelector(
      `product-form-component[data-product-id="${this.#productId}"]`
    );
    
    if (!this.#productFormComponent) {
      // Try to find any product form component on product pages
      this.#productFormComponent = document.querySelector('product-form-component');
    }

    if (!this.#productFormComponent) return;

    // Find the form
    this.#form = this.#productFormComponent.querySelector('form');
    if (!this.#form) return;

    // Find the original add to cart button
    this.#originalButton = this.#form.querySelector('button[name="add"]');
    if (!this.#originalButton) return;

    // Set up intersection observer to watch the original button
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Original button is visible, hide sticky button
            this.#hide();
          } else {
            // Original button is not visible, show sticky button
            this.#show();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    this.#observer.observe(this.#originalButton);

    // Set up scroll handler for better performance
    this.#scrollHandler = this.#checkVisibility.bind(this);
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });

    // Listen for variant updates to update price
    this.#variantUpdateHandler = this.#handleVariantUpdate.bind(this);
    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    }

    // Handle window resize to show/hide on mobile/desktop switch
    this.#resizeHandler = this.#handleResize.bind(this);
    window.addEventListener('resize', this.#resizeHandler, { passive: true });

    // Set up click handler
    const button = this.querySelector('.sticky-add-to-cart__button');
    if (button) {
      button.addEventListener('click', this.#handleClick.bind(this));
    }

    // Initial check
    this.#checkVisibility();
  }

  #cleanup() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }

    if (this.#variantUpdateHandler) {
      const section = this.closest('.shopify-section');
      if (section) {
        section.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
      }
      this.#variantUpdateHandler = null;
    }

    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }
  }

  #handleResize() {
    // Hide on desktop, show on mobile if conditions are met
    if (window.innerWidth >= 750) {
      this.hidden = true;
      this.#isVisible = false;
    } else {
      this.#checkVisibility();
    }
  }

  #checkVisibility() {
    if (window.innerWidth >= 750) {
      this.hidden = true;
      return;
    }

    if (!this.#originalButton) return;

    const rect = this.#originalButton.getBoundingClientRect();
    const isInViewport =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);

    if (isInViewport) {
      this.#hide();
    } else {
      this.#show();
    }
  }

  #show() {
    if (this.#isVisible || window.innerWidth >= 750) return;
    
    const canAddToCart = this.dataset.canAddToCart === 'true';
    if (!canAddToCart) return;

    // Use requestAnimationFrame to ensure smooth animation
    requestAnimationFrame(() => {
      this.hidden = false;
      this.#isVisible = true;
    });
  }

  #hide() {
    if (!this.#isVisible) return;
    
    // Use requestAnimationFrame to ensure smooth animation
    requestAnimationFrame(() => {
      this.hidden = true;
      this.#isVisible = false;
    });
  }

  #handleClick(event) {
    event.preventDefault();
    
    if (!this.#originalButton) return;

    // Click the original button to trigger the form submission
    this.#originalButton.click();
  }

  #handleVariantUpdate(event) {
    if (!(event instanceof VariantUpdateEvent)) return;
    
    // Check if this update is for our product
    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && eventProductId !== this.#productId) {
      return;
    }

    const variant = event.detail?.resource;
    if (!variant) return;

    // Update the variant ID
    if (variant.id) {
      this.dataset.variantId = variant.id;
    }

    // Update price from the HTML fragment
    const html = event.detail?.data?.html;
    if (html) {
      const priceContainer = html.querySelector('product-price [ref="priceContainer"]');
      if (priceContainer) {
        const priceElement = priceContainer.querySelector('.price');
        if (priceElement) {
          const priceText = priceElement.textContent.trim();
          const priceElementSticky = this.querySelector('.sticky-add-to-cart__price');
          if (priceElementSticky) {
            priceElementSticky.textContent = priceText;
          }
        }

        // Update compare at price
        const comparePriceElement = priceContainer.querySelector('.compare-at-price');
        const comparePriceElementSticky = this.querySelector('.sticky-add-to-cart__compare-price');
        
        if (comparePriceElement && comparePriceElement.textContent.trim()) {
          const comparePriceText = comparePriceElement.textContent.trim();
          if (!comparePriceElementSticky) {
            // Create compare price element if it doesn't exist
            const priceWrapper = this.querySelector('.sticky-add-to-cart__price-wrapper');
            if (priceWrapper) {
              const newComparePrice = document.createElement('span');
              newComparePrice.className = 'sticky-add-to-cart__compare-price';
              newComparePrice.textContent = comparePriceText;
              priceWrapper.appendChild(newComparePrice);
            }
          } else {
            comparePriceElementSticky.textContent = comparePriceText;
          }
        } else if (comparePriceElementSticky) {
          // Remove compare price if it no longer exists
          comparePriceElementSticky.remove();
        }
      }
    } else if (variant.price) {
      // Fallback: use variant price directly
      this.#updatePriceFromVariant(variant);
    }

    // Update availability
    const available = variant.available !== false;
    this.dataset.canAddToCart = available;
    
    const button = this.querySelector('.sticky-add-to-cart__button');
    if (button) {
      button.disabled = !available;
      
      // Update button text if sold out
      const textElement = this.querySelector('.sticky-add-to-cart__text');
      if (textElement && !available) {
        textElement.textContent = document.querySelector('.add-to-cart-text__content')?.textContent || 'Sold out';
      }
    }

    // Update form variant ID
    if (this.#form) {
      const variantInput = this.#form.querySelector('input[name="id"]');
      if (variantInput && variant.id) {
        variantInput.value = variant.id;
      }
    }
  }

  #updatePriceFromVariant(variant) {
    const priceElement = this.querySelector('.sticky-add-to-cart__price');
    if (!priceElement || !variant.price) return;

    // Use the price from the variant (in cents)
    // The price should already be formatted in the initial render
    // This is just a fallback
    const priceInDollars = variant.price / 100;
    const formattedPrice = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2,
    }).format(priceInDollars);

    priceElement.textContent = formattedPrice;

    // Update compare at price if available
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const comparePriceInDollars = variant.compare_at_price / 100;
      const formattedComparePrice = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: 2,
      }).format(comparePriceInDollars);

      let comparePriceElementSticky = this.querySelector('.sticky-add-to-cart__compare-price');
      if (!comparePriceElementSticky) {
        const priceWrapper = this.querySelector('.sticky-add-to-cart__price-wrapper');
        if (priceWrapper) {
          comparePriceElementSticky = document.createElement('span');
          comparePriceElementSticky.className = 'sticky-add-to-cart__compare-price';
          priceWrapper.appendChild(comparePriceElementSticky);
        }
      }
      if (comparePriceElementSticky) {
        comparePriceElementSticky.textContent = formattedComparePrice;
      }
    } else {
      // Remove compare price if it no longer applies
      const comparePriceElementSticky = this.querySelector('.sticky-add-to-cart__compare-price');
      if (comparePriceElementSticky) {
        comparePriceElementSticky.remove();
      }
    }
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);

